/* ---------------------------------------------------------------------------
   "Add inspection" — the dialog that appends one entry to a bridge's
   inspection history from the detail screen. Unlike editDialog.ts, this
   never edits an existing entry: inspections are a dated log, each one
   a fact recorded once and kept as-is.

   Rendered next to editDialog into src/main.ts's dialog layer, on the same "no
   lifecycle of its own" basis.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS } from '../core/dom.ts';
import { actions, MAX_INSPECTION_PHOTOS, type InspectionDraft, type State } from '../core/store.ts';
import { button, field, scrim, seg } from '../components/widgets.ts';
import { findBridge, inspectionTypeLabel } from '../data/derive.ts';
import { approxKb, downscale } from '../core/downscale.ts';
import {
  BRIDGE_ELEMENTS,
  CUSTOM_ELEMENT_MAX,
  ELEMENT_GROUPS,
  elementLabel,
  type BridgeElement,
} from '../data/elements.ts';
import { EXTENT_DEFINITIONS, URGENCY_DEFINITIONS } from '../data/ratingGuide.ts';
import { INSPECTION_TRIGGERS, INSPECTION_TYPES, needsTrigger } from '../data/inspectionTypes.ts';

/**
 * The rating systems real DoR inventory sheets show up under — a bridge rated
 * "3" means something different on each. Fixed choices rather than free text:
 * scale is which system a rating is measured against, not a second number.
 */
const SCALE_OPTIONS = ['0-4', '0-10', '0-100'] as const;

/**
 * Whether moving from one draft to the next changes anything this dialog draws
 * other than the contents of the control the change came from.
 *
 * Only the scale does: it names the range in the rating field's label, and lights
 * one of three buttons. The type does only where it moves between a type that names
 * a trigger and one that does not (LC-60). Date, rating and note are drawn by
 * nothing but their own controls, which already hold what was typed into them — so src/main.ts can
 * leave the DOM alone entirely and typing costs no render at all.
 */
export function inspectionDialogNeedsPaint(a: InspectionDraft, b: InspectionDraft): boolean {
  return (
    a.scale !== b.scale ||
    a.photos !== b.photos ||
    needsTrigger(a.type) !== needsTrigger(b.type) ||
    a.customElements !== b.customElements ||
    elementGates(a) !== elementGates(b)
  );
}

/**
 * Which element rows have their extent and urgency controls open, as one string.
 *
 * `QC-01` gave the condition box the power to enable or disable the two beside
 * it, which broke the rule the function above exists to keep: typing into a
 * control that already shows what was typed must cost no render. Comparing the
 * conditions themselves would reinstate a render per keystroke — every digit of
 * a `2` on a 0-100 sheet.
 *
 * So this compares only what the row's condition CHANGES ON SCREEN: whether it
 * is blank, `N`, or a rating. Typing `3` over `2` moves nothing and paints
 * nothing; clearing it, or typing `N`, flips a gate and paints once.
 */
function elementGates(d: InspectionDraft): string {
  return [...BRIDGE_ELEMENTS.map((e) => e.id), ...d.customElements].map((id) => {
    const v = (d.elements[id] ?? '').trim();
    return v === '' ? '-' : v.toUpperCase() === 'N' ? 'N' : 'r';
  }).join('');
}

/**
 * Condition, extent and urgency per element — DoR's three boxes.
 *
 * `QC-01`. Every Routine and Principal Inspection form scores each line item on
 * three scales, not one, and this dialog offered only the first of them. The
 * second is the quantitative axis of a DoR inspection — extent is DEFINED as a
 * percentage of the element's length or area — and the third is what `GR-02`
 * needs to rank a work programme by anything better than "overdue".
 *
 * Every element is offered and none is required: an inspector rates what they
 * looked at, and a blank means *not assessed* rather than zero — which on every
 * scale the sheet uses is a real and bad score. Eleven rows is the whole set of
 * items on DoR's inspection form (src/data/elements.ts), grouped and ordered the
 * way the form prints them so the screen and the paper agree about which item is
 * item 5.
 *
 * There is deliberately no live "3 of 11 assessed" count. It would have to be
 * redrawn on every keystroke, and the rule this dialog already follows is that
 * typing into a control which already shows what was typed costs no render at
 * all — see inspectionDialogNeedsPaint.
 */
function elementField(d: InspectionDraft): HTMLElement {
  const max = d.scale ? d.scale.split('-')[1] : undefined;

  return h(
    'div',
    { class: 'dlg__block' },
    field(
      max
        ? `Condition, extent and urgency — optional, condition out of ${max}`
        : 'Condition, extent and urgency — optional',
      h(
        'div',
        {},
        h(
          'div',
          { class: 'elgrid__legend' },
          h('span', { class: 'elgrid__legend-c', text: 'C' }),
          h('span', { text: ' condition · ' }),
          h('span', { class: 'elgrid__legend-c', text: 'E' }),
          h('span', { text: ' extent · ' }),
          h('span', { class: 'elgrid__legend-c', text: 'U' }),
          h('span', { text: ' urgency — the three boxes on the DoR form' }),
        ),
        ...ELEMENT_GROUPS.map((group) => {
          const members = BRIDGE_ELEMENTS.filter((e) => e.group === group);
          const isOther = group === 'Other';
          if (!members.length && !isOther) return null;
          const custom: BridgeElement[] = isOther
            ? d.customElements.map((id) => ({ id, label: elementLabel(id), group }))
            : [];
          return h(
            'div',
            { class: 'elgrid__group' },
            h('div', { class: 'elgrid__head', text: group }),
            h(
              'div',
              { class: 'elgrid' },
              ...members.map((element) => elementRow(d, element, max)),
              ...custom.map((element) => elementRow(d, element, max, true)),
              isOther ? customAddRow() : null,
            ),
          );
        }),
      ),
    ),
  );
}

/**
 * One element's row: label, condition, extent, urgency.
 *
 * Condition is a TEXT input, not a number one, and that is the whole reason this
 * is a function rather than three lines inline. It has to be able to hold `N` —
 * DoR's *not applicable, or element not accessible for inspection* — which a
 * `type="number"` control cannot carry at all, and the alternatives are worse: a
 * `<select>` of every value works at 0-4 and is unusable at 0-100, and a separate
 * N toggle is a fourth control on every one of eleven rows. `inputmode="numeric"`
 * still brings up the number pad on a phone, which is what the number type was
 * actually buying here.
 *
 * Extent and urgency are `<select>`s because they are BANDS rather than
 * measurements. Five named states each, with DoR's own definition on the option's
 * title — an extent of 2.5 is not halfway between "5 to 20%" and "more than 20%",
 * it is a value that names no band, and a number input invites one.
 */
function elementRow(
  d: InspectionDraft,
  element: BridgeElement,
  max: string | undefined,
  removable = false,
): HTMLElement {
  const condition = d.elements[element.id] ?? '';
  /* The guideline's own rule, enforced where the inspector can see it rather
     than only on submit: an element marked N has no extent and no urgency, so
     the two controls are disabled rather than left to be refused later. */
  const notAssessable = condition.trim().toUpperCase() === 'N';
  const rated = condition.trim() !== '' && !notAssessable;

  return h(
    'div',
    { class: 'elgrid__row', ...(element.hint ? { title: element.hint } : {}) },
    /* Before the label rather than after the boxes, so the three columns stay
       lined up with the form's own rows above. */
    removable
      ? h('button', {
          type: 'button',
          class: 'elgrid__remove',
          title: 'Remove this item',
          'aria-label': `Remove ${element.label}`,
          text: '✕',
          onclick: () => actions.removeCustomElement(element.id),
        })
      : null,
    h('span', {
      class: 'elgrid__label ellipsis',
      text: element.formItem ? `${element.formItem}. ${element.label}` : element.label,
      ...(removable ? { title: element.label } : {}),
    }),
    h('input', {
      class: 'input elgrid__input',
      type: 'text',
      inputmode: 'numeric',
      maxlength: '4',
      placeholder: '—',
      'aria-label': `${element.label} condition${max ? `, out of ${max}, or N` : ', or N'}`,
      title: max ? `Condition, 0 to ${max}, or N for not applicable / not accessible` : 'Condition, or N',
      value: condition,
      oninput: (e: Event) =>
        actions.setElementBox(element.id, 'rating', (e.target as HTMLInputElement).value),
    }),
    bandSelect(d, element, 'extent', EXTENT_DEFINITIONS, rated),
    bandSelect(d, element, 'urgency', URGENCY_DEFINITIONS, rated),
  );
}

/**
 * Where an inspector names an item the form does not have. The name lives only
 * in the input until it is added, so typing it costs no render; Enter adds it
 * rather than submitting the whole inspection.
 */
function customAddRow(): HTMLElement {
  const input = h('input', {
    class: 'input elgrid__add-input',
    type: 'text',
    maxlength: String(CUSTOM_ELEMENT_MAX),
    placeholder: 'Add an item not on the form',
    'aria-label': 'Name of an item to add',
    onkeydown: (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      add();
    },
  }) as HTMLInputElement;
  const add = () => {
    if (actions.addCustomElement(input.value)) input.value = '';
  };

  return h(
    'div',
    { class: 'elgrid__row elgrid__add' },
    input,
    button('Add', { sm: true, onClick: add }),
  );
}

/** One 0–4 band picker, disabled until there is a condition beside it to qualify. */
function bandSelect(
  d: InspectionDraft,
  element: BridgeElement,
  box: 'extent' | 'urgency',
  definitions: readonly string[],
  enabled: boolean,
): HTMLElement {
  const value = (box === 'extent' ? d.extents : d.urgencies)[element.id] ?? '';

  return h(
    'select',
    {
      class: 'select elgrid__band',
      disabled: !enabled,
      'aria-label': `${element.label} ${box}`,
      title: enabled
        ? `${box[0]!.toUpperCase()}${box.slice(1)} — 0 to 4`
        : 'Rate the condition first. An extent with no condition beside it says nothing about the element.',
      onchange: (e: Event) =>
        actions.setElementBox(element.id, box, (e.target as HTMLSelectElement).value),
    },
    h('option', { value: '', selected: value === '', text: '—' }),
    ...definitions.map((definition, band) =>
      h('option', {
        value: String(band),
        selected: value === String(band),
        title: definition,
        text: String(band),
      }),
    ),
  );
}

/**
 * Photographs on the inspection being recorded.
 *
 * Not `imageSlot`. That component is a local preview whose object URLs die with
 * the page — which was the whole finding here — and it holds its pictures in a
 * module-level map the submit path cannot reach. These live on the draft, and go
 * up with the entry.
 *
 * The downscale happens on pick rather than on submit, deliberately: it is where
 * a file the browser cannot decode can be reported against the file the person
 * just chose, and it means the size under each thumbnail is the size that will
 * actually be sent.
 */
function photoField(d: InspectionDraft): HTMLElement {
  const full = d.photos.length >= MAX_INSPECTION_PHOTOS;

  const input = h('input', {
    type: 'file',
    accept: 'image/*',
    /* Straight to the rear camera on a phone, instead of the file picker. The
       photograph an inspector wants is the one they have not taken yet, and a
       chooser full of screenshots is two taps away from it.

       `capture` is a HINT, not a mode: a browser that does not honour it falls
       back to the picker, and `multiple` still means several existing files can
       be attached at a desk. Nothing is lost by asking. */
    capture: 'environment',
    multiple: true,
    class: 'visually-hidden',
    onchange: (e: Event) => {
      const target = e.target as HTMLInputElement;
      const files = Array.from(target.files ?? []);
      target.value = '';
      void (async () => {
        for (const file of files) {
          try {
            const shrunk = await downscale(file);
            actions.addInspectionPhoto(shrunk.dataUrl);
          } catch (err) {
            actions.setInspectionError(err instanceof Error ? err.message : 'That file could not be read.');
          }
        }
      })();
    },
  });

  return h(
    'div',
    { class: 'dlg__block' },
    field(
      d.photos.length
        ? `Photographs (${d.photos.length} of ${MAX_INSPECTION_PHOTOS})`
        : 'Photographs',
      h(
        'div',
        {},
        h(
          'div',
          { class: 'insp-photos' },
          ...d.photos.map((dataUrl, i) =>
            h(
              'span',
              { class: 'insp-photos__item' },
              h('img', { class: 'insp-photos__img', src: dataUrl, alt: `Photograph ${i + 1}` }),
              h('span', { class: 'insp-photos__size', text: `${approxKb(dataUrl)} kB` }),
              h('button', {
                type: 'button',
                class: 'imgslot__clear',
                title: 'Remove this photograph',
                'aria-label': `Remove photograph ${i + 1}`,
                text: '✕',
                onclick: () => actions.removeInspectionPhoto(i),
              }),
            ),
          ),
          full
            ? null
            : h(
                'button',
                {
                  type: 'button',
                  class: 'insp-photos__add',
                  title: 'Attach a photograph',
                  onclick: (e: Event) =>
                    (e.currentTarget as HTMLElement).querySelector<HTMLInputElement>('input')?.click(),
                },
                icon(ICONS.image, { size: 16, stroke: 'var(--txt3)' }),
                h('span', { text: 'Add' }),
                input,
              ),
        ),
        full
          ? h('div', {
              class: 'dlg__hint',
              text: `That is the limit of ${MAX_INSPECTION_PHOTOS} for one inspection.`,
            })
          : null,
      ),
    ),
  );
}

/** The dialog, or null when it is closed. */
export function inspectionDialog(s: Readonly<State>): HTMLElement | null {
  if (!s.addingInspectionId) return null;
  const b = findBridge(s.addingInspectionId);
  /* No record to hang this on, which happens only where the register is
     empty. Closed rather than drawn over nothing — the same answer this
     dialog already gives when the thing it was opened for has gone. */
  if (b === null) return null;
  const d = s.inspectionDraft;

  const setField = (key: keyof InspectionDraft) => (e: Event) =>
    actions.setInspectionField(
      key,
      (e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value,
    );

  return h(
    'div',
    {
      class: 'dlg',
      ...scrim(() => actions.closeAddInspection(), () => s.inspectionSaving),
    },
    h(
      'form',
      {
        // --compact: the longest form in the app, at the metrics a dense form
        // wants and using the whole window. See .dlg__panel--compact in app.css.
        class: 'dlg__panel dlg__panel--sm dlg__panel--compact',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': `Add inspection — ${b.name}`,
        onsubmit: (e: Event) => {
          e.preventDefault();
          void actions.saveInspection();
        },
      },

      h(
        'div',
        { class: 'dlg__head' },
        h(
          'div',
          {},
          h('div', { class: 'dlg__kicker', text: `${b.id} · inspection history` }),
          h('h2', { class: 'dlg__title', text: 'Add inspection' }),
        ),
        h('span', { class: 'spacer' }),
        h(
          'button',
          {
            type: 'button',
            class: 'dlg__close',
            title: 'Cancel',
            'aria-label': 'Cancel',
            onclick: () => actions.closeAddInspection(),
          },
          '×',
        ),
      ),

      s.inspectionError
        ? h('div', { class: 'dlg__note dlg__note--error' }, h('span', { text: s.inspectionError }))
        : null,

      h(
        'div',
        { class: 'dlg__body' },
        h(
          'div',
          { class: 'dlg__form-grid' },
          field(
            'Date',
            h('input', {
              id: 'insp-date',
              class: 'input',
              type: 'date',
              required: true,
              value: d.date,
              oninput: setField('date'),
            }),
            'insp-date',
          ),
          field(
            'Type',
            h(
              'select',
              { id: 'insp-type', class: 'select', onchange: setField('type') },
              ...INSPECTION_TYPES.map((t) =>
                h('option', { value: t, selected: t === d.type, text: inspectionTypeLabel(t) }),
              ),
            ),
            'insp-type',
          ),
          needsTrigger(d.type)
            ? field(
                'Trigger',
                h(
                  'select',
                  { id: 'insp-trigger', class: 'select', required: true, onchange: setField('trigger') },
                  h('option', { value: '', selected: d.trigger === '', text: 'Choose…' }),
                  ...INSPECTION_TRIGGERS.map((t) =>
                    h('option', { value: t.id, selected: t.id === d.trigger, text: t.label }),
                  ),
                ),
                'insp-trigger',
              )
            : null,
          field(
            'Scale',
            seg(
              SCALE_OPTIONS.map((opt) => ({
                label: opt,
                on: d.scale === opt,
                onClick: () => actions.setInspectionField('scale', opt),
              })),
            ),
          ),
          field(
            // Names which system the number below is measured against, so the
            // two fields can't be mistaken for two ratings.
            d.scale ? `Rating (out of ${d.scale.split('-')[1]})` : 'Rating',
            h('input', {
              id: 'insp-rating',
              class: 'input',
              type: 'number',
              step: 'any',
              required: true,
              value: d.rating,
              oninput: setField('rating'),
            }),
            'insp-rating',
          ),
          h(
            'div',
            { style: 'grid-column: 1 / -1' },
            field(
              'Note',
              h('textarea', {
                id: 'insp-note',
                class: 'input',
                rows: 3,
                placeholder: 'What the inspection found',
                required: true,
                value: d.note,
                oninput: setField('note'),
              }),
              'insp-note',
            ),
          ),
        ),

        elementField(d),

        photoField(d),
      ),

      h(
        'div',
        { class: 'dlg__foot' },
        h('span', { class: 'spacer' }),
        button('Cancel', { onClick: () => actions.closeAddInspection(), disabled: s.inspectionSaving }),
        button(s.inspectionSaving ? 'Saving…' : 'Add inspection', {
          type: 'submit',
          primary: true,
          disabled: s.inspectionSaving,
        }),
      ),
    ),
  );
}

